import React, { useEffect, useState, useRef } from 'react';
import { Row, Col, Card, Button, Form, Table } from 'react-bootstrap';
import { Formik, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { toast } from '../../common/Toast/toast';
import {
  createApps,
  updateApps,
  deleteApps,
  getApps,
  getAppsById,
} from '../../../../services/AppServices';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { confirm } from '../../common/Confirm/confirm';

const validationSchema = Yup.object().shape({
  label: Yup.string().required('Label is required'),
  value: Yup.string().required('Value is required'),
  url: Yup.string().required('URL is required'),
});

/* The palette's own tokens rather than a second theme of this
   screen's own. These were literal oranges, so these pages and their
   popups stayed orange whichever palette was chosen while the app
   around them moved. ThemeContext writes the --pg-* custom properties
   onto the document, and a custom property works in an inline style
   exactly as it does in a stylesheet, so naming them here is the whole
   of the fix.

   Ink is deliberately not a palette value (see css/theme/palettes.css):
   it holds its contrast under every palette, so it stays literal. */
const THEME = {
  primary: 'var(--pg-accent)',
  primaryDark: 'var(--pg-accent-hover)',
  cardBg: 'var(--pg-card)',
  border: 'var(--pg-line)',
};

const FilterSection = ({ title, children }) => (
  <div
    className="filter-section mb-4 p-4 bg-dark-2"
    style={{
      borderRadius: '15px',
      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
      // background: THEME.cardBg,
      border: `1px solid ${THEME.border}`,
    }}
  >
    <h5
      className="mb-3"
      style={{
        borderBottom: `2px solid ${THEME.primary}`,
        paddingBottom: '8px',
        color: THEME.primaryDark,
      }}
    >
      {title}
    </h5>
    {children}
  </div>
);

// Renders as a page on /setting/add-app and /setting/edit-app/:id, and as
// the body of a dialog when AppList opens it. With none of these props it
// reads the URL and navigates on finish, exactly as the route always did.
function AddApp({
  // Shown inside a dialog rather than as a page: the component drops its
  // own header, card and back-link, since the dialog supplies them.
  embedded = false,
  // The record to edit. Falls back to the :id route param.
  recordId,
  // Called after a successful save instead of navigating away.
  onDone,
  // Called when the form is dismissed instead of navigating away.
  onCancel,
}) {
  const { id: routeId } = useParams();
  const id = recordId ?? routeId;
  const navigate = useNavigate();

  // Leaving the form means "go back to the list" on the route, and "close
  // the dialog" when embedded. Every exit goes through these two so the
  // component never has to know which it is.
  const leave = () => {
    if (onCancel) onCancel();
    else navigate('/setting/list-apps');
  };

  const finish = () => {
    if (onDone) onDone();
    else navigate('/setting/list-apps');
  };
  const [apps, setApps] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [initialValues, setInitialValues] = useState({
    label: '',
    value: '',
    url: '',
  });
  const formikBagRef = useRef(null);

  // Fetch apps
  const fetchApps = async () => {
    try {
      const res = await getApps(1000, 1, '');
      setApps(res.data || []);
    } catch (error) {
      toast.error('Failed to fetch apps');
    }
  };

  useEffect(() => {
    if (id) {
      (async () => {
        try {
          const res = await getAppsById(id);
          const data = res.data;
          setEditingId(id);
          setInitialValues({
            label: data.label || '',
            value: data.value || '',
            url: data.url || '',
          });
          if (formikBagRef.current) {
            formikBagRef.current.setValues({
              label: data.label || '',
              value: data.value || '',
              url: data.url || '',
            });
          }
        } catch (error) {
          toast.error('Failed to fetch app details');
        }
      })();
    } else {
      setEditingId(null);
      setInitialValues({ label: '', value: '', url: '' });
    }
  }, [id]);

  // Edit handler
  const handleEdit = async (id) => {
    try {
      const res = await getAppsById(id);
      const data = res.data;
      setEditingId(id);
      setInitialValues({
        label: data.label || '',
        value: data.value || '',
        url: data.url || '',
      });
      if (formikBagRef.current) {
        formikBagRef.current.setValues({
          label: data.label || '',
          value: data.value || '',
          url: data.url || '',
        });
      }
    } catch (error) {
      toast.error('Failed to fetch app details');
    }
  };

  // Delete handler
  const handleDelete = async (id) => {
    const result = await confirm({
      title: 'Are you sure?',
      tone: 'danger',
      text: "You won't be able to revert this!",
      confirmButtonText: 'Yes, delete it!',
    });

    if (!result.isConfirmed) return;

    try {
      await deleteApps(id);
      toast.success('App deleted successfully!');
      fetchApps();
    } catch (error) {
      toast.error('Failed to delete app');
    }
  };

  // Submit handler
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const payload = {
        label: values.label,
        value: values.value,
        url: values.url,
      };
      if (id) {
        await updateApps(id, payload);
        toast.success('App updated successfully!');
      } else {
        await createApps(payload);
        toast.success('App added successfully!');
      }
      setEditingId(null);
      setInitialValues({ label: '', value: '', url: '' });
      resetForm();
      finish();
    } catch (error) {
      toast.error('Error processing app!');
    } finally {
      setSubmitting(false);
    }
  };

  // The form itself. The dialog shows only this; the route wraps it in
  // the page chrome below.
  const formBody = (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {(formik) => {
        const { values, isSubmitting, setFieldValue, errors, touched, resetForm } = formik;
        formikBagRef.current = formik;
        return (
          <Form onSubmit={formik.handleSubmit}>
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group controlId="formLabel">
                  <Form.Label style={{ color: THEME.primaryDark }}>
                    Label <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="label"
                    value={values.label}
                    onChange={(e) => {
                      const label = e.target.value;
                      const processed = label.toLowerCase().replace(/\s+/g, '-');
                      setFieldValue('label', label);
                      setFieldValue('value', processed);
                    }}
                    placeholder="Enter label (e.g., my-app)"
                    isInvalid={touched.label && errors.label}
                  />
                  <ErrorMessage name="label" component="div" className="text-danger" />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="formValue">
                  <Form.Label style={{ color: THEME.primaryDark }}>
                    Value <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="value"
                    value={values.value}
                    onChange={(e) =>
                      setFieldValue('value', e.target.value.replace(/\s+/g, '-'))
                    }
                    placeholder="Enter value (e.g., my-app)"
                    isInvalid={touched.value && errors.value}
                  />
                  <ErrorMessage name="value" component="div" className="text-danger" />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="formUrl">
                  <Form.Label style={{ color: THEME.primaryDark }}>
                    URL <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="url"
                    value={values.url}
                    onChange={(e) => setFieldValue('url', e.target.value)}
                    placeholder="Enter URL (e.g., /my-app)"
                    isInvalid={touched.url && errors.url}
                  />
                  <ErrorMessage name="url" component="div" className="text-danger" />
                </Form.Group>
              </Col>
            </Row>
            <div className="d-flex justify-content-end mt-4">
              <Button
                variant="secondary"
                onClick={leave}
                className="me-2"
                style={{ borderRadius: '30px', padding: '8px 25px' }}
              >
                Cancel
              </Button>

              <Button
                variant="success"
                type="submit"
                disabled={isSubmitting}
                style={{
                  backgroundColor: THEME.primary,
                  borderColor: THEME.primary,
                  borderRadius: '30px',
                  padding: '8px 30px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                }}
              >
                {isSubmitting ? 'Processing...' : editingId ? 'Update App' : 'Add App'}
              </Button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );

  // In a dialog the head, the padding and the card belong to the dialog,
  // and a second toast host would stack a duplicate of every message the
  // list page behind it is already showing.
  if (embedded) return formBody;

  return (
    <div style={{ padding: '20px' }}>
      <Link to="/setting/list-apps">
        <Button
          style={{
            backgroundColor: THEME.primary,
            borderColor: THEME.primary,
            borderRadius: '30px',
            padding: '8px 20px',
          }}
          className="mb-5"
        >
          Go to App List
        </Button>
      </Link>

      <Card className="border-0 shadow-sm" style={{ borderRadius: '15px', overflow: 'hidden' }}>
        <Card.Body className="bg-dark">
          <FilterSection title={editingId ? 'Edit App' : 'Add App'}>
            {formBody}
          </FilterSection>
        </Card.Body>
      </Card>
    </div>
  );
}
export default AddApp;
